import { Grid } from '~/engine/data/common/Grid';
import { RoiReadModel } from '../readmodels/StructureSetReadModel';
import { ReversibleOperation, RoiMementoFactory } from './';
import {
    BASELINE_STATE_INDEX,
    INITIAL_STATE_OPERATION,
    UndoRedoManager,
} from './UndoRedoManager';

export type ContourOperationResult = {
    compressedPixels: Uint8Array;
    grid: Grid;
    roiNumber: number;
    structureSetUid: string;
};

/** One retained step, as the history list renders it. */
export type ContourHistoryEntry = {
    /** Pass back to {@link ContourUndoService.jumpTo} to restore this step. */
    index: number;
    /** Raw operation label, e.g. 'margin' or {@link INITIAL_STATE_OPERATION}. */
    operation: string;
    timestamp: Date;
    /** The step the ROI's contour currently reflects. */
    isCurrent: boolean;
    /** The state before this session's first edit of the ROI. */
    isInitial: boolean;
};

/**
 * A restore produced by {@link ContourUndoService.jumpTo}. `direction` says
 * whether the target sits before or after the step the ROI was on, so the
 * caller can publish the matching undo/redo event; both restore pixels
 * identically, and the distinction exists for listeners that care which way
 * the user moved.
 */
export type ContourJump = {
    result: ContourOperationResult;
    direction: 'backward' | 'forward';
};

/**
 * Composite key for an ROI's undo stack. A DICOM ROINumber is only unique
 * WITHIN one structure set (numbering restarts at 1 per set) and a patient can
 * have several loaded at once, while these stacks outlive a set switch — they
 * are cleared only on patient unload. Keyed by ROINumber alone, selecting ROI 1
 * in a second set would resolve to the first set's stack: its edits would be
 * listed as this structure's history, and a single undo would write one
 * structure's mask onto another. Scoping by the owning set's UID keeps each
 * set's history its own.
 *
 * Mirrors the key of the sibling session store, {@link RoiBaselineStore}.
 */
function stackKey(structureSetUid: string, roiNumber: number): string {
    // A DICOM UID is only digits and dots, so a space unambiguously separates it
    // from the (integer) ROINumber.
    return `${structureSetUid} ${roiNumber}`;
}

/**
 * Service for contour undo/redo operations using full pixel snapshots.
 * Stores complete compressed pixel data for each operation.
 */
export class ContourUndoService {
    private roiStacks = new Map<string, UndoRedoManager>();
    private globalListeners = new Set<() => void>();

    private notifyGlobalListeners(): void {
        this.globalListeners.forEach((listener) => listener());
    }

    private getStack(
        structureSetUid: string,
        roiNumber: number
    ): UndoRedoManager | undefined {
        return this.roiStacks.get(stackKey(structureSetUid, roiNumber));
    }

    private getOrCreateStack(
        structureSetUid: string,
        roiNumber: number
    ): UndoRedoManager {
        const key = stackKey(structureSetUid, roiNumber);
        if (!this.roiStacks.has(key)) {
            const stack = new UndoRedoManager();
            stack.subscribe(() => this.notifyGlobalListeners());
            this.roiStacks.set(key, stack);
        }
        return this.roiStacks.get(key)!;
    }

    private extractOperationResult(
        operation: ReversibleOperation
    ): ContourOperationResult | undefined {
        const state = operation.getState();
        if (!state.compressedContourPixels || !state.grid) {
            return undefined;
        }
        return {
            compressedPixels: state.compressedContourPixels,
            grid: state.grid,
            roiNumber: operation.getRoiNumber(),
            structureSetUid: operation.getStructureSetUid(),
        };
    }

    /**
     * For React hook to subscribe to global undo/redo state changes
     */
    subscribe(listener: () => void): { unsubscribe: () => void } {
        this.globalListeners.add(listener);
        listener(); // Call immediately
        return {
            unsubscribe: () => {
                this.globalListeners.delete(listener);
            },
        };
    }

    /**
     * Add a contour operation to the ROI's undo stack
     * @param structureSetUid - UID of the structure set the ROI belongs to
     * @param operation - Operation name, use 'initial-state' for the first capture
     */
    async addContourOperation(
        structureSetUid: string,
        roiReadModel: RoiReadModel,
        pixels: Uint8Array[],
        grid: Grid,
        operation: string
    ): Promise<void> {
        const description =
            operation === INITIAL_STATE_OPERATION
                ? `Initial state for ROI ${roiReadModel.roiNumber} (${roiReadModel.name})`
                : `${operation} on ROI ${roiReadModel.roiNumber} (${roiReadModel.name})`;

        const memento = await RoiMementoFactory.fromReadModelWithContours(
            roiReadModel,
            pixels,
            grid
        );

        const reversibleOp = new ReversibleOperation(
            memento,
            description,
            operation
        );
        const stack = this.getOrCreateStack(
            structureSetUid,
            roiReadModel.roiNumber
        );
        stack.addOperation(reversibleOp);
    }

    /**
     * Undo the last contour operation for a specific ROI
     */
    undo(
        structureSetUid: string,
        roiNumber: number
    ): ContourOperationResult | undefined {
        const stack = this.getStack(structureSetUid, roiNumber);
        if (!stack) {
            return undefined;
        }

        const operation = stack.undo();
        if (!operation) {
            return undefined;
        }

        return this.extractOperationResult(operation);
    }

    /**
     * Redo the last undone contour operation for a specific ROI
     */
    redo(
        structureSetUid: string,
        roiNumber: number
    ): ContourOperationResult | undefined {
        const stack = this.getStack(structureSetUid, roiNumber);
        if (!stack) {
            return undefined;
        }

        const operation = stack.redo();
        if (!operation) {
            return undefined;
        }

        return this.extractOperationResult(operation);
    }

    /**
     * The ROI's snapshot immediately before its most recent operation, without
     * disturbing the undo/redo pointer. Returns undefined when the ROI has no
     * prior state. Used to render a "what the last operation changed" diff
     * overlay against the live contour.
     */
    peekPrevious(
        structureSetUid: string,
        roiNumber: number
    ): ContourOperationResult | undefined {
        const stack = this.getStack(structureSetUid, roiNumber);
        if (!stack) {
            return undefined;
        }
        const operation = stack.peekPrevious();
        if (!operation) {
            return undefined;
        }
        return this.extractOperationResult(operation);
    }

    /**
     * Every retained step for an ROI, oldest first. Empty when the ROI has no
     * history. Read-only: it never moves the undo pointer.
     */
    getHistory(
        structureSetUid: string,
        roiNumber: number
    ): ContourHistoryEntry[] {
        const stack = this.getStack(structureSetUid, roiNumber);
        if (!stack) {
            return [];
        }
        return stack.getHistory().map((entry) => ({
            index: entry.index,
            operation: entry.operation.getOperation(),
            timestamp: entry.operation.getTimestamp(),
            isCurrent: entry.isCurrent,
            isInitial: entry.isBaseline,
        }));
    }

    /**
     * How many intermediate steps were dropped to keep the ROI's stack within
     * its size cap. Non-zero means {@link getHistory} has a gap between the
     * initial state and the oldest retained edit, and the UI must say so
     * rather than present the list as complete.
     */
    getDiscardedStepCount(structureSetUid: string, roiNumber: number): number {
        return (
            this.getStack(structureSetUid, roiNumber)?.discardedStepCount ?? 0
        );
    }

    /**
     * Restore an ROI to an arbitrary retained step. Returns undefined when the
     * index is not in the ROI's history or the ROI is already on it, so a
     * no-op jump publishes no restore event.
     */
    jumpTo(
        structureSetUid: string,
        roiNumber: number,
        index: number
    ): ContourJump | undefined {
        const stack = this.getStack(structureSetUid, roiNumber);
        if (!stack) {
            return undefined;
        }

        const from = stack.currentStateIndex;
        if (index === from) {
            return undefined;
        }

        const operation = stack.jumpToState(index);
        if (!operation) {
            return undefined;
        }

        const result = this.extractOperationResult(operation);
        if (!result) {
            return undefined;
        }
        return { result, direction: index < from ? 'backward' : 'forward' };
    }

    /**
     * Restore an ROI to the state it was in before this session's first edit.
     * The steps jumped over stay redoable until the next edit is recorded, so
     * this is recoverable.
     *
     * Declines when the stack holds no baseline: index 0 is then an ordinary
     * edit, and restoring it under this name would present a half-edited
     * contour as the original.
     */
    revertToInitial(
        structureSetUid: string,
        roiNumber: number
    ): ContourJump | undefined {
        if (!this.getStack(structureSetUid, roiNumber)?.hasBaseline) {
            return undefined;
        }
        return this.jumpTo(structureSetUid, roiNumber, BASELINE_STATE_INDEX);
    }

    /** True when the ROI has edits to revert, i.e. it is not already initial. */
    canRevertToInitial(structureSetUid: string, roiNumber: number): boolean {
        const stack = this.getStack(structureSetUid, roiNumber);
        return (
            !!stack &&
            stack.hasBaseline &&
            stack.currentStateIndex > BASELINE_STATE_INDEX
        );
    }

    canUndo(structureSetUid: string, roiNumber: number): boolean {
        const stack = this.getStack(structureSetUid, roiNumber);
        return stack ? stack.canUndo() : false;
    }

    canRedo(structureSetUid: string, roiNumber: number): boolean {
        const stack = this.getStack(structureSetUid, roiNumber);
        return stack ? stack.canRedo() : false;
    }

    /**
     * Drops the undo/redo history of a single ROI number. MUST be called when
     * an ROI is deleted: {@link DicomStructureSet.getNextFreeRoiNumber} reissues
     * the LOWEST free ROINumber, so the next structure created after a deletion
     * inherits the deleted one's number — and with it, a stack whose snapshots
     * hold the DELETED structure's pixels. A single Ctrl+Z would then republish
     * a removed contour onto an unrelated new structure, silently resurrecting
     * clinical data the user deleted on purpose. The same stale stack also feeds
     * the 'pre-operation' difference overlay via {@link peekPrevious}.
     *
     * Scoped by the owning set's UID (see {@link stackKey}), so this drops the
     * history of one structure in one set and leaves a same-numbered ROI in
     * another loaded set untouched.
     */
    clearRoi(structureSetUid: string, roiNumber: number): void {
        const key = stackKey(structureSetUid, roiNumber);
        const stack = this.roiStacks.get(key);
        if (!stack) {
            return;
        }
        stack.clear();
        this.roiStacks.delete(key);
        this.notifyGlobalListeners();
    }

    clear(): void {
        this.roiStacks.forEach((stack) => stack.clear());
        this.roiStacks.clear();
        this.notifyGlobalListeners();
    }

    isRoiInitialized(structureSetUid: string, roiNumber: number): boolean {
        const stack = this.getStack(structureSetUid, roiNumber);
        return stack ? stack.size > 0 : false;
    }
}
